import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, Loader2, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

const Blank = ({ answer, show, testId }) => (
  <span data-testid={testId} className="block min-h-[1.75rem] border-b-2 border-dashed border-black px-1 text-center font-bold">
    {show ? <span className="text-emerald-700">{answer}</span> : "\u00A0"}
  </span>
);

export default function MapWorksheet() {
  const { id } = useParams();
  const [term, setTerm] = useState(null);
  const [error, setError] = useState(false);
  const [level, setLevel] = useState("facile");
  const [bank, setBank] = useState(true);
  const [answers, setAnswers] = useState(false);

  useEffect(() => { api.get(`/terms/${id}`).then((r) => setTerm(r.data)).catch(() => setError(true)); }, [id]);

  const map = term?.concept_map;
  const words = useMemo(() => {
    if (!map) return [];
    const w = map.branches.map((b) => b.label);
    if (level === "difficile") map.branches.forEach((b) => w.push(...b.children));
    return [...w].sort((a, b) => a.localeCompare(b, "it"));
  }, [map, level]);

  if (error) return <p className="py-24 text-center font-bold">Parola non trovata.</p>;
  if (!term) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!map) return <p className="py-24 text-center font-bold" data-testid="worksheet-no-map">Questa parola non ha ancora una mappa concettuale.</p>;

  const opt = (active) => `rounded-full border-2 border-[var(--ink)] px-4 py-1.5 text-sm font-extrabold ${active ? "bg-[var(--ink)] text-white" : "bg-white"}`;

  return (
    <main className="mx-auto max-w-4xl px-5 pb-24 pt-10 sm:px-8 print:max-w-none print:p-0" data-testid="worksheet-page">
      <div className="ink-box mb-10 flex flex-wrap items-center gap-3 rounded-3xl bg-white p-5 print:hidden">
        <Link to={`/termine/${term.id}`} data-testid="worksheet-back-link" className="flex items-center gap-1 font-extrabold hover:underline"><ArrowLeft className="h-4 w-4" /> Torna alla parola</Link>
        <div className="flex gap-2">
          <button onClick={() => setLevel("facile")} data-testid="worksheet-level-easy" className={opt(level === "facile")}>Facile</button>
          <button onClick={() => setLevel("difficile")} data-testid="worksheet-level-hard" className={opt(level === "difficile")}>Difficile</button>
        </div>
        <label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" checked={bank} onChange={(e) => setBank(e.target.checked)} data-testid="worksheet-word-bank" className="h-4 w-4" /> Banca delle parole</label>
        <label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" checked={answers} onChange={(e) => setAnswers(e.target.checked)} data-testid="worksheet-show-answers" className="h-4 w-4" /> Soluzioni</label>
        <button onClick={() => window.print()} data-testid="worksheet-print-button" className="ink-box-sm ink-press ml-auto flex items-center gap-2 rounded-full bg-[var(--sky)] px-5 py-2 font-extrabold text-white"><Printer className="h-4 w-4" /> Stampa / Salva PDF</button>
        <p className="w-full text-xs font-semibold text-neutral-500">Facile: completa i concetti. Difficile: completa anche le idee collegate.</p>
      </div>

      <header className="mb-6 border-b-[3px] border-black pb-3">
        <p className="text-xs font-extrabold uppercase tracking-widest text-neutral-600">Mappa da completare · {term.subject}</p>
        <h1 className="font-display text-3xl font-bold">{map.center}</h1>
        <p className="mt-1 font-bold">Nome: ______________________ Classe: ______ Data: ___________</p>
      </header>

      {bank && (
        <div className="mb-6 rounded-2xl border-2 border-black p-3" data-testid="worksheet-bank">
          <p className="text-xs font-extrabold uppercase tracking-widest">Banca delle parole</p>
          <p className="mt-1 text-sm font-semibold">{words.join(" · ")}</p>
        </div>
      )}

      <div className="flex flex-col items-center">
        <div className="rounded-full border-[3px] border-black px-8 py-3"><span className="font-display text-2xl font-bold">{map.center}</span></div>
        <div className="h-5 w-[3px] bg-black" />
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 print:grid-cols-3" data-testid="worksheet-map">
        {map.branches.map((b, i) => (
          <div key={i} className="break-inside-avoid rounded-2xl border-[2.5px] border-black p-3" data-testid={`worksheet-branch-${i}`}>
            {b.link && <p className="text-center text-xs font-extrabold italic">{b.link}</p>}
            <Blank answer={b.label} show={answers} testId={`worksheet-blank-${i}`} />
            <ul className="mt-3 space-y-2 text-sm">
              {b.children.map((c, j) => (
                <li key={j} className={`font-semibold ${level === "difficile" ? "pt-1" : "rounded-lg border-2 border-black px-2 py-1"}`}>
                  {level === "difficile" ? <Blank answer={c} show={answers} testId={`worksheet-blank-${i}-${j}`} /> : c}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </main>
  );
}
